import {
  Box,
  Flex,
  Grid,
  HStack,
  Icon,
  Image as ChakraImage,
  Input,
  Stack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { Area, Point } from 'react-easy-crop'
import Cropper from 'react-easy-crop'
import { useTranslation } from 'react-i18next'
import {
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuChevronUp,
  LuCrop,
  LuCrosshair,
  LuDroplet,
  LuFlipHorizontal,
  LuFlipVertical,
  LuInfo,
  LuMinus,
  LuPlus,
  LuRotateCcw,
  LuRotateCw,
} from 'react-icons/lu'

import {
  type BlurBrushImperativeHandle,
  BlurSelection,
} from '@/components/atoms/Image/BlurSelection'
import { Button } from '@/components/ui/button'
import { useColorModeValue } from '@/components/ui/color-mode'
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Slider } from '@/components/ui/slider'
import {
  bakeImageHorizontalFlip,
  bakeImageVerticalFlip,
  type CropExportBackground,
  EXPORT_JPEG_QUALITY,
  exportCroppedImage,
} from '@/utils/exportCroppedImage'

interface ImageCropperModalProps {
  isOpen: boolean
  onUpload: (
    file: File,
    closeModal: () => void,
    metadata?: { legend?: string; alt?: string }
  ) => void
  onClose: () => void
  rawFile?: File
  preview?: string
  crop?: boolean
  blur?: boolean
  metadata?: boolean
  legend?: string
  alt?: string
  aspectRatio?: number | 'free'
  /** Marges hors image : blanc (JPEG) ou transparence (PNG) */
  outputBackground?: CropExportBackground
}

type CropSnapshot = {
  crop: Point
  zoom: number
  rotation: number
  imageSrc: string
}

const CROP_NUDGE = 12
const MAX_CROP_UNDO = 30

const normalizeRotationDeg = (deg: number) => ((deg % 360) + 360) % 360

/** Slider centered on 0°: [-180, 180] for display, matches normalized angle [0, 360). */
const rotationToSliderSigned = (deg: number): number => {
  const n = normalizeRotationDeg(deg)
  return n <= 180 ? n : n - 360
}

const CHECKERBOARD_BG = {
  backgroundColor: '#e8e8e8',
  backgroundImage: `
    linear-gradient(45deg, #d0d0d0 25%, transparent 25%),
    linear-gradient(-45deg, #d0d0d0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #d0d0d0 75%),
    linear-gradient(-45deg, transparent 75%, #d0d0d0 75%)
  `,
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
} as const

const snapshotsEqual = (a: CropSnapshot, b: CropSnapshot) =>
  a.zoom === b.zoom &&
  a.crop.x === b.crop.x &&
  a.crop.y === b.crop.y &&
  a.rotation === b.rotation &&
  a.imageSrc === b.imageSrc

export const PimpImageModal: React.FC<ImageCropperModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  rawFile,
  preview,
  crop = true,
  blur = true,
  metadata = true,
  legend: initialLegend = '',
  alt: initialAlt = '',
  aspectRatio = 1,
  outputBackground = 'white',
}: ImageCropperModalProps) => {
  const clamp = useCallback(
    (value: number, min: number, max: number) =>
      Math.min(max, Math.max(min, value)),
    []
  )
  const [imageSrc, setImageSrc] = useState('')
  const [cropPosition, setCropPosition] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [flipBusy, setFlipBusy] = useState(false)
  const [mode, setMode] = useState<'crop' | 'blur' | 'metadata'>(
    (crop && 'crop') || (blur && 'blur') || (metadata && 'metadata') || 'crop'
  )
  const [legend, setLegend] = useState(initialLegend)
  const [alt, setAlt] = useState(initialAlt)

  const blurBrushRef = useRef<BlurBrushImperativeHandle>(null)
  const cropPositionRef = useRef(cropPosition)
  const zoomRef = useRef(zoom)
  const rotationRef = useRef(rotation)
  const imageSrcRef = useRef('')
  const interactionBaselineRef = useRef<CropSnapshot | null>(null)
  const zoomSliderDraggingRef = useRef(false)
  const rotationSliderDraggingRef = useRef(false)
  const cropPastRef = useRef<CropSnapshot[]>([])
  const cropFutureRef = useRef<CropSnapshot[]>([])
  const { t } = useTranslation()

  useEffect(() => {
    cropPositionRef.current = cropPosition
  }, [cropPosition])
  useEffect(() => {
    zoomRef.current = zoom
  }, [zoom])
  useEffect(() => {
    rotationRef.current = rotation
  }, [rotation])
  useEffect(() => {
    imageSrcRef.current = imageSrc
  }, [imageSrc])

  const captureSnapshot = useCallback((): CropSnapshot => {
    return {
      crop: { ...cropPositionRef.current },
      zoom: zoomRef.current,
      rotation: rotationRef.current,
      imageSrc: imageSrcRef.current,
    }
  }, [])

  const pushUndo = useCallback((snapshotBeforeAction: CropSnapshot) => {
    cropPastRef.current.push(snapshotBeforeAction)
    if (cropPastRef.current.length > MAX_CROP_UNDO) {
      cropPastRef.current.shift()
    }
    cropFutureRef.current = []
  }, [])

  useEffect(() => {
    setImageSrc('')

    if (rawFile) {
      const fileReader = new FileReader()
      fileReader.addEventListener('load', (e) => {
        const result = e.target?.result as string
        if (result && result.trim() !== '') {
          setImageSrc(result)
        }
      })
      fileReader.addEventListener('error', (e) => {
        console.error('Error reading file:', e)
      })
      fileReader.readAsDataURL(rawFile)
    } else if (preview && preview.trim() !== '') {
      const image = new Image()
      image.crossOrigin = 'anonymous'
      image.addEventListener('load', () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = image.width
        canvas.height = image.height

        if (ctx) {
          ctx.drawImage(image, 0, 0)
          const dataURL = canvas.toDataURL('image/jpeg', EXPORT_JPEG_QUALITY)
          setImageSrc(dataURL)
        } else {
          setImageSrc(preview)
        }
      })
      image.addEventListener('error', (e) => {
        console.error('Error loading image:', e)
        setImageSrc(preview)
      })
      image.src = preview
    }
  }, [rawFile, preview])

  useEffect(() => {
    if (!isOpen) {
      setCropPosition({ x: 0, y: 0 })
      setZoom(1)
      setCroppedAreaPixels(null)
      cropPastRef.current = []
      cropFutureRef.current = []
      interactionBaselineRef.current = null
      setLegend(initialLegend)
      setAlt(initialAlt)
    }
  }, [isOpen, initialLegend, initialAlt])

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixelsArg: Area) => {
      setCroppedAreaPixels(croppedAreaPixelsArg)
    },
    []
  )

  const onCropInteractionStart = useCallback(() => {
    interactionBaselineRef.current = captureSnapshot()
  }, [captureSnapshot])

  const onCropInteractionEnd = useCallback(() => {
    const base = interactionBaselineRef.current
    interactionBaselineRef.current = null
    if (!base) {
      return
    }
    const now = captureSnapshot()
    if (!snapshotsEqual(base, now)) {
      pushUndo(base)
    }
  }, [captureSnapshot, pushUndo])

  const undoCrop = useCallback(() => {
    const past = cropPastRef.current
    if (past.length === 0) {
      return
    }
    const prev = past.pop()!
    cropFutureRef.current.push(captureSnapshot())
    setCropPosition(prev.crop)
    setZoom(prev.zoom)
    setRotation(prev.rotation)
    setImageSrc(prev.imageSrc)
  }, [captureSnapshot])

  const redoCrop = useCallback(() => {
    const future = cropFutureRef.current
    if (future.length === 0) {
      return
    }
    const next = future.pop()!
    cropPastRef.current.push(captureSnapshot())
    setCropPosition(next.crop)
    setZoom(next.zoom)
    setRotation(next.rotation)
    setImageSrc(next.imageSrc)
  }, [captureSnapshot])

  const onCropHandler = useCallback(async () => {
    if (!imageSrc) {
      return
    }

    try {
      const { blob, filename, mimeType } = await exportCroppedImage(
        imageSrc,
        croppedAreaPixels,
        {
          background: outputBackground,
          jpegQuality: EXPORT_JPEG_QUALITY,
          rotation: normalizeRotationDeg(rotation),
        }
      )
      const imageFile = new File([blob], filename, { type: mimeType })
      onUpload(imageFile, onClose, { legend, alt })
    } catch (error) {
      console.error('Error cropping image:', error)
    }
  }, [
    imageSrc,
    croppedAreaPixels,
    onUpload,
    onClose,
    outputBackground,
    rotation,
    legend,
    alt,
  ])

  const onBlur = useCallback(() => {
    const canvas = blurBrushRef.current?.getCanvas()
    if (!canvas) {
      throw new Error('Canvas ref is not defined')
    }
    canvas.toBlob((blob) => {
      if (blob) {
        onUpload(
          new File([blob], 'image.png', { type: 'image/png' }),
          onClose,
          { legend, alt }
        )
      }
    }, 'image/png')
  }, [onUpload, onClose, legend, alt])

  const onSave = () => {
    if (mode === 'crop') {
      onCropHandler()
    } else if (mode === 'blur') {
      onBlur()
    } else {
      // If we're in metadata mode, we need to decide if we use the current source (maybe cropped already)
      if (rawFile) {
        onUpload(rawFile, onClose, { legend, alt })
      } else {
        // Fallback for previews
        onCropHandler()
      }
    }
  }

  const cropAspectRatio = aspectRatio === 'free' ? undefined : aspectRatio

  const handleZoomChange = useCallback(
    (nextZoom: number) => {
      setZoom(clamp(nextZoom, 0.2, 5))
    },
    [clamp]
  )

  const handleRotationSliderChange = useCallback((nextDeg: number) => {
    setRotation(normalizeRotationDeg(Math.round(nextDeg)))
  }, [])

  const nudgeCrop = useCallback(
    (deltaX: number, deltaY: number) => {
      const start = captureSnapshot()
      setCropPosition((prev) => {
        const next = { x: prev.x + deltaX, y: prev.y + deltaY }
        const after: CropSnapshot = {
          crop: next,
          zoom: zoomRef.current,
          rotation: rotationRef.current,
          imageSrc: imageSrcRef.current,
        }
        if (!snapshotsEqual(start, after)) {
          pushUndo(start)
        }
        return next
      })
    },
    [captureSnapshot, pushUndo]
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey
      if (!mod) {
        return
      }
      const key = e.key.toLowerCase()
      const isUndo = key === 'z' && !e.shiftKey
      const isRedo = key === 'y' || (key === 'z' && e.shiftKey)

      if (mode === 'blur' && (isUndo || isRedo)) {
        e.preventDefault()
        if (isUndo) {
          blurBrushRef.current?.undo()
        } else {
          blurBrushRef.current?.redo()
        }
        return
      }

      if (mode === 'crop' && crop && (isUndo || isRedo)) {
        e.preventDefault()
        if (isUndo) {
          undoCrop()
        } else {
          redoCrop()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () =>
      window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [isOpen, mode, crop, undoCrop, redoCrop])

  const dialogBg = useColorModeValue('white', 'blue.800')
  const panelBg = useColorModeValue('gray.50', 'whiteAlpha.50')
  const panelBorder = useColorModeValue('gray.100', 'whiteAlpha.100')
  const segmentBg = useColorModeValue('gray.100', 'whiteAlpha.100')
  const segmentBorder = useColorModeValue('gray.200', 'whiteAlpha.200')
  const sectionLabelColor = useColorModeValue('gray.600', 'gray.400')

  const resetCropCenter = useCallback(() => {
    const start = captureSnapshot()
    const next = { x: 0, y: 0 }
    const after: CropSnapshot = {
      crop: next,
      zoom: zoomRef.current,
      rotation: rotationRef.current,
      imageSrc: imageSrcRef.current,
    }
    if (!snapshotsEqual(start, after)) {
      pushUndo(start)
    }
    setCropPosition(next)
  }, [captureSnapshot, pushUndo])

  const rotateBy = useCallback(
    (deltaDeg: number) => {
      pushUndo(captureSnapshot())
      setRotation((prev) => normalizeRotationDeg(prev + deltaDeg))
    },
    [captureSnapshot, pushUndo]
  )

  const flipHorizontal = useCallback(async () => {
    if (flipBusy || !imageSrc) {
      return
    }
    pushUndo(captureSnapshot())
    setFlipBusy(true)
    try {
      const next = await bakeImageHorizontalFlip(imageSrc)
      setImageSrc(next)
      setCropPosition({ x: 0, y: 0 })
      setZoom(1)
      setRotation(0)
    } catch (e) {
      console.error('Flip error:', e)
    } finally {
      setFlipBusy(false)
    }
  }, [captureSnapshot, flipBusy, imageSrc, pushUndo])

  const flipVertical = useCallback(async () => {
    if (flipBusy || !imageSrc) {
      return
    }
    pushUndo(captureSnapshot())
    setFlipBusy(true)
    try {
      const next = await bakeImageVerticalFlip(imageSrc)
      setImageSrc(next)
      setCropPosition({ x: 0, y: 0 })
      setZoom(1)
      setRotation(0)
    } catch (e) {
      console.error('Flip error:', e)
    } finally {
      setFlipBusy(false)
    }
  }, [captureSnapshot, flipBusy, imageSrc, pushUndo])

  const sectionLabel = (label: string) => (
    <Text
      fontSize="10px"
      fontWeight="semibold"
      color={sectionLabelColor}
      letterSpacing="0.08em"
      textTransform="uppercase"
      mb={2}
    >
      {label}
    </Text>
  )

  const toolIconBtnProps = {
    variant: 'ghost' as const,
    size: 'sm' as const,
    h: '32px',
    w: '32px',
    minW: '32px',
    p: 0,
  }

  const switchToCropMode = useCallback(() => {
    if (mode === 'blur' && blur) {
      const ref = blurBrushRef.current
      ref?.commitPendingStroke()
      if (ref?.hasModifications()) {
        const canvas = ref.getCanvas()
        if (canvas?.width && canvas?.height) {
          setImageSrc(canvas.toDataURL('image/png'))
          setCropPosition({ x: 0, y: 0 })
          setZoom(1)
          setRotation(0)
          setCroppedAreaPixels(null)
          cropPastRef.current = []
          cropFutureRef.current = []
        }
      }
    }
    setMode('crop')
  }, [mode, blur])

  const modeToggle =
    crop || blur || metadata ? (
      <Box
        borderRadius="lg"
        borderWidth="1px"
        borderColor={segmentBorder}
        bg={segmentBg}
        p="3px"
        maxW="480px"
        mb={3}
      >
        <HStack gap="3px">
          {crop && (
            <Button
              type="button"
              flex={1}
              h="32px"
              size="sm"
              fontWeight="medium"
              borderRadius="md"
              variant={mode === 'crop' ? 'subtle' : 'ghost'}
              colorPalette="gray"
              onClick={switchToCropMode}
              disabled={!imageSrc}
            >
              <HStack gap="2" justify="center">
                <Icon as={LuCrop} boxSize={3.5} />
                <Text fontSize="sm">{t('media.image.actions.mode.crop')}</Text>
              </HStack>
            </Button>
          )}
          {blur && (
            <Button
              type="button"
              flex={1}
              h="32px"
              size="sm"
              fontWeight="medium"
              borderRadius="md"
              variant={mode === 'blur' ? 'subtle' : 'ghost'}
              colorPalette="gray"
              onClick={() => setMode('blur')}
              disabled={!imageSrc}
            >
              <HStack gap="2" justify="center">
                <Icon as={LuDroplet} boxSize={3.5} />
                <Text fontSize="sm">{t('media.image.actions.mode.blur')}</Text>
              </HStack>
            </Button>
          )}
          {metadata && (
            <Button
              type="button"
              flex={1}
              h="32px"
              size="sm"
              fontWeight="medium"
              borderRadius="md"
              variant={mode === 'metadata' ? 'subtle' : 'ghost'}
              colorPalette="gray"
              onClick={() => setMode('metadata')}
              disabled={!imageSrc}
            >
              <HStack gap="2" justify="center">
                <Icon as={LuInfo} boxSize={3.5} />
                <Text fontSize="sm">
                  {t('media.image.actions.mode.metadata')}
                </Text>
              </HStack>
            </Button>
          )}
        </HStack>
      </Box>
    ) : null

  return (
    <DialogRoot
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          onClose()
        }
      }}
      placement={'center'}
      closeOnInteractOutside={true}
      // Non-modal mode avoids global page locks (scroll lock / aria-hidden) that can remain stuck.
      modal={false}
    >
      <DialogContent
        bg={dialogBg}
        maxW="min(96vw, 940px)"
        w="full"
        maxH="min(96dvh, 980px)"
        display="flex"
        flexDirection="column"
      >
        <DialogHeader>{t('media.image.actions.modal.title')}</DialogHeader>
        <DialogCloseTrigger />
        <DialogBody overflowY="auto">
          {!imageSrc ? (
            <div
              style={{
                minHeight: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {t('global:loading')}
            </div>
          ) : (
            <Box>
              {modeToggle}
              <Text fontSize="xs" color="fg.muted" mb={3} lineHeight="short">
                {mode === 'blur'
                  ? t('media.image.actions.blur.shortcutsHint')
                  : mode === 'metadata'
                    ? t('media.image.actions.metadata.description')
                    : t('media.image.actions.shortcutsHint')}
              </Text>
              {mode === 'blur' ? (
                <BlurSelection ref={blurBrushRef} imageSrc={imageSrc} />
              ) : null}
              {mode === 'metadata' ? (
                <VStack gap={6} align="stretch" py={4}>
                  <Box
                    borderRadius="12px"
                    overflow="hidden"
                    borderWidth="1px"
                    borderColor={panelBorder}
                    maxH="200px"
                    bg={CHECKERBOARD_BG.backgroundColor}
                  >
                    <ChakraImage
                      src={imageSrc}
                      alt=""
                      w="full"
                      h="full"
                      objectFit="contain"
                    />
                  </Box>
                  <Stack gap={5}>
                    <Field
                      label={t('media.image.fields.legend.label')}
                      helperText={t('media.image.fields.legend.helperText')}
                    >
                      <Input
                        value={legend}
                        onChange={(e) => setLegend(e.target.value)}
                        placeholder={t('media.image.fields.legend.placeholder')}
                      />
                    </Field>
                    <Field
                      label={t('media.image.fields.alt.label')}
                      helperText={t('media.image.fields.alt.helperText')}
                    >
                      <Textarea
                        value={alt}
                        onChange={(e) => setAlt(e.target.value)}
                        placeholder={t('media.image.fields.alt.placeholder')}
                        rows={3}
                      />
                    </Field>
                  </Stack>
                </VStack>
              ) : null}
              {mode === 'crop' ? (
                <>
                  <Box
                    position="relative"
                    w="full"
                    minH="220px"
                    h="clamp(240px, 42dvh, 520px)"
                    maxH="520px"
                    borderRadius="12px"
                    overflow="hidden"
                  >
                    <Cropper
                      image={imageSrc}
                      crop={cropPosition}
                      zoom={zoom}
                      rotation={rotation}
                      aspect={cropAspectRatio}
                      onCropChange={setCropPosition}
                      onZoomChange={handleZoomChange}
                      onRotationChange={setRotation}
                      onCropComplete={onCropComplete}
                      onInteractionStart={onCropInteractionStart}
                      onInteractionEnd={onCropInteractionEnd}
                      restrictPosition={false}
                      showGrid={true}
                      zoomSpeed={0.35}
                      style={{
                        containerStyle: {
                          ...CHECKERBOARD_BG,
                          borderRadius: 12,
                        },
                      }}
                      zoomWithScroll={true}
                      cropShape="rect"
                      minZoom={0.2}
                      maxZoom={5}
                    />
                  </Box>

                  <Box
                    mt={4}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={panelBorder}
                    bg={panelBg}
                    px={{ base: 4, sm: 5 }}
                    py={{ base: 4, sm: 5 }}
                    mx={{ base: 1, sm: 2 }}
                    my={2}
                  >
                    <VStack align="stretch" gap={0}>
                      <Box
                        pb={5}
                        borderBottomWidth="1px"
                        borderColor={panelBorder}
                      >
                        {sectionLabel(t('media.image.actions.zoom'))}
                        <HStack gap={2} align="center">
                          <Button
                            {...toolIconBtnProps}
                            aria-label={t('media.image.actions.zoomOut')}
                            onClick={() => {
                              pushUndo(captureSnapshot())
                              setZoom((z) => clamp(z - 0.15, 0.2, 5))
                            }}
                            disabled={zoom <= 0.2}
                          >
                            <Icon
                              as={LuMinus}
                              boxSize={3.5}
                              strokeWidth={1.75}
                            />
                          </Button>
                          <Box
                            flex={1}
                            onPointerDownCapture={() => {
                              if (!zoomSliderDraggingRef.current) {
                                zoomSliderDraggingRef.current = true
                                pushUndo(captureSnapshot())
                              }
                            }}
                            onPointerUpCapture={() => {
                              zoomSliderDraggingRef.current = false
                            }}
                            onPointerCancelCapture={() => {
                              zoomSliderDraggingRef.current = false
                            }}
                          >
                            <Slider
                              min={0.2}
                              max={5}
                              step={0.05}
                              value={[zoom]}
                              onValueChange={(details) =>
                                handleZoomChange(details.value[0])
                              }
                            />
                          </Box>
                          <Button
                            {...toolIconBtnProps}
                            aria-label={t('media.image.actions.zoomIn')}
                            onClick={() => {
                              pushUndo(captureSnapshot())
                              setZoom((z) => clamp(z + 0.15, 0.2, 5))
                            }}
                            disabled={zoom >= 5}
                          >
                            <Icon
                              as={LuPlus}
                              boxSize={3.5}
                              strokeWidth={1.75}
                            />
                          </Button>
                          <Text
                            fontSize="xs"
                            color="fg.muted"
                            minW="44px"
                            textAlign="right"
                            fontVariantNumeric="tabular-nums"
                          >
                            {Math.round(zoom * 100)}%
                          </Text>
                        </HStack>
                      </Box>
                      <Box py={5} px={1} mx={1} my={2}>
                        <Grid
                          templateColumns={{ base: '1fr', md: '4fr 1fr' }}
                          gap={{ base: 4, md: 5 }}
                          alignItems="start"
                        >
                          <Box minW={0}>
                            {sectionLabel(
                              t('media.image.actions.orientation.title')
                            )}
                            <HStack gap={2} flexWrap="wrap" align="center">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                h="32px"
                                onClick={() => rotateBy(-90)}
                                disabled={flipBusy}
                                aria-label={t(
                                  'media.image.actions.orientation.rotateLeft'
                                )}
                              >
                                <HStack gap={1}>
                                  <Icon as={LuRotateCcw} boxSize={3.5} />
                                  <Text fontSize="xs">
                                    {t(
                                      'media.image.actions.orientation.rotateMinus90'
                                    )}
                                  </Text>
                                </HStack>
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                h="32px"
                                onClick={() => rotateBy(90)}
                                disabled={flipBusy}
                                aria-label={t(
                                  'media.image.actions.orientation.rotateRight'
                                )}
                              >
                                <HStack gap={1}>
                                  <Icon as={LuRotateCw} boxSize={3.5} />
                                  <Text fontSize="xs">
                                    {t(
                                      'media.image.actions.orientation.rotatePlus90'
                                    )}
                                  </Text>
                                </HStack>
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                h="32px"
                                onClick={() => void flipHorizontal()}
                                disabled={flipBusy}
                                aria-label={t(
                                  'media.image.actions.orientation.flipHorizontal'
                                )}
                              >
                                <HStack gap={1}>
                                  <Icon as={LuFlipHorizontal} boxSize={3.5} />
                                  <Text
                                    fontSize="xs"
                                    display={{ base: 'none', sm: 'inline' }}
                                  >
                                    {t(
                                      'media.image.actions.orientation.flipHorizontalShort'
                                    )}
                                  </Text>
                                </HStack>
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                h="32px"
                                onClick={() => void flipVertical()}
                                disabled={flipBusy}
                                aria-label={t(
                                  'media.image.actions.orientation.flipVertical'
                                )}
                              >
                                <HStack gap={1}>
                                  <Icon as={LuFlipVertical} boxSize={3.5} />
                                  <Text
                                    fontSize="xs"
                                    display={{ base: 'none', sm: 'inline' }}
                                  >
                                    {t(
                                      'media.image.actions.orientation.flipVerticalShort'
                                    )}
                                  </Text>
                                </HStack>
                              </Button>
                            </HStack>
                            <Text
                              fontSize="xs"
                              color="fg.muted"
                              mt={3}
                              mb={1.5}
                            >
                              {t(
                                'media.image.actions.orientation.rotationSlider'
                              )}
                            </Text>
                            <HStack gap={2} align="center">
                              <Box
                                flex={1}
                                onPointerDownCapture={() => {
                                  if (!rotationSliderDraggingRef.current) {
                                    rotationSliderDraggingRef.current = true
                                    pushUndo(captureSnapshot())
                                  }
                                }}
                                onPointerUpCapture={() => {
                                  rotationSliderDraggingRef.current = false
                                }}
                                onPointerCancelCapture={() => {
                                  rotationSliderDraggingRef.current = false
                                }}
                              >
                                <Slider
                                  min={-180}
                                  max={180}
                                  step={1}
                                  value={[rotationToSliderSigned(rotation)]}
                                  onValueChange={(details) =>
                                    handleRotationSliderChange(details.value[0])
                                  }
                                />
                              </Box>
                              <Text
                                fontSize="xs"
                                color="fg.muted"
                                minW="48px"
                                textAlign="right"
                                fontVariantNumeric="tabular-nums"
                              >
                                {normalizeRotationDeg(rotation)}°
                              </Text>
                            </HStack>
                          </Box>
                          <Box
                            minW={{ base: 'auto', md: '72px' }}
                            borderLeftWidth={{ base: 0, md: '1px' }}
                            borderTopWidth={{ base: '1px', md: 0 }}
                            borderColor={panelBorder}
                            pl={{ base: 0, md: 4 }}
                            pt={{ base: 4, md: 0 }}
                          >
                            {sectionLabel(
                              t('media.image.actions.position.title')
                            )}
                            <Flex justify="center" align="flex-start" pt={1}>
                              <VStack gap="1" align="center">
                                <Button
                                  {...toolIconBtnProps}
                                  aria-label={t(
                                    'media.image.actions.position.up'
                                  )}
                                  onClick={() => nudgeCrop(0, -CROP_NUDGE)}
                                >
                                  <Icon
                                    as={LuChevronUp}
                                    boxSize={3.5}
                                    strokeWidth={1.75}
                                  />
                                </Button>
                                <HStack gap="1">
                                  <Button
                                    {...toolIconBtnProps}
                                    aria-label={t(
                                      'media.image.actions.position.left'
                                    )}
                                    onClick={() => nudgeCrop(-CROP_NUDGE, 0)}
                                  >
                                    <Icon
                                      as={LuChevronLeft}
                                      boxSize={3.5}
                                      strokeWidth={1.75}
                                    />
                                  </Button>
                                  <Button
                                    {...toolIconBtnProps}
                                    aria-label={t(
                                      'media.image.actions.position.reset'
                                    )}
                                    onClick={resetCropCenter}
                                  >
                                    <Icon
                                      as={LuCrosshair}
                                      boxSize={3.5}
                                      strokeWidth={1.75}
                                    />
                                  </Button>
                                  <Button
                                    {...toolIconBtnProps}
                                    aria-label={t(
                                      'media.image.actions.position.right'
                                    )}
                                    onClick={() => nudgeCrop(CROP_NUDGE, 0)}
                                  >
                                    <Icon
                                      as={LuChevronRight}
                                      boxSize={3.5}
                                      strokeWidth={1.75}
                                    />
                                  </Button>
                                </HStack>
                                <Button
                                  {...toolIconBtnProps}
                                  aria-label={t(
                                    'media.image.actions.position.down'
                                  )}
                                  onClick={() => nudgeCrop(0, CROP_NUDGE)}
                                >
                                  <Icon
                                    as={LuChevronDown}
                                    boxSize={3.5}
                                    strokeWidth={1.75}
                                  />
                                </Button>
                              </VStack>
                            </Flex>
                          </Box>
                        </Grid>
                      </Box>
                    </VStack>
                  </Box>
                </>
              ) : null}
            </Box>
          )}
        </DialogBody>
        <DialogFooter>
          <HStack w="full" justify="flex-end" gap={2} flexWrap="wrap">
            <Button variant="ghost" onClick={onClose}>
              {t('global:cancel')}
            </Button>
            <Button
              colorPalette="brand"
              onClick={onSave}
              disabled={!imageSrc || (mode === 'crop' && !croppedAreaPixels)}
            >
              {t('global:save')}
            </Button>
          </HStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
